import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import {
  MagnifyingGlass, Sparkle, Globe, Translate, Target, ArrowRight,
  Lightning, ChartBar, Tag, Crown, Star, ArrowUpRight, Robot, Flask, Megaphone, Trophy,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'pt', label: 'Português (PT + BR)' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
];

const COUNTRY_CODES = ['PT', 'BR', 'ES', 'MX', 'AR', 'US', 'UK'];

const NICHE_SUGGESTIONS = [
  'weight_loss',
  'digital_marketing',
  'finance',
  'english',
  'relationships',
  'fitness_home',
  'baking',
  'trading',
];

const scoreColor = (score) => {
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#F59E0B';
  return '#EF4444';
};

const TypeBadge = ({ type }) => {
  const { t } = useTranslation();
  const map = {
    sales_page: { label: t('disc.type_sales_page'), color: '#3B82F6' },
    landing: { label: 'Landing', color: '#06B6D4' },
    vsl: { label: 'VSL', color: '#A855F7' },
    webinar: { label: 'Webinar', color: '#F59E0B' },
    quiz_funnel: { label: 'Quiz Funnel', color: '#EC4899' },
  };
  const cfg = map[type] || { label: type || t('disc.type_fallback'), color: '#9CA3AF' };
  return (
    <span
      className="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
      style={{ background: `${cfg.color}22`, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
};

const ResultCard = ({ item, onAnalyze }) => {
  const { t } = useTranslation();
  const score = item.estimated_score || 0;
  const color = scoreColor(score);
  return (
    <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl hover:border-white/20 transition group" data-testid="discovery-result-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <TypeBadge type={item.type} />
            {item.brand_or_creator && (
              <span className="text-[11px] text-white/40 uppercase tracking-wider">{item.brand_or_creator}</span>
            )}
          </div>
          <h3 className="font-[Outfit] font-bold text-base text-white line-clamp-2">{item.title}</h3>
        </div>
        <div
          className="flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center border"
          style={{ background: `${color}11`, borderColor: `${color}44` }}
        >
          <span className="text-lg font-[Outfit] font-bold leading-none" style={{ color }}>{score}</span>
          <span className="text-[9px] uppercase tracking-wider text-white/40">score</span>
        </div>
      </div>

      <p className="text-sm text-white/60 leading-relaxed mb-4 line-clamp-3">{item.why_modelable}</p>

      {item.patterns_detected && item.patterns_detected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {item.patterns_detected.slice(0, 5).map((p) => (
            <span key={p} className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-[10px] text-white/60">
              {p}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-white/5 flex-wrap gap-2">
        {item.estimated_price_eur ? (
          <div className="text-xs text-white/50">
            {t('disc.est_price')} <span className="text-white font-semibold">€{item.estimated_price_eur}</span>
          </div>
        ) : <span className="text-[10px] text-white/30 uppercase tracking-wider">{item.discovery_method?.replace('_', ' ') || ''}</span>}
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {(item.enriched_queries || []).slice(0, 4).map((q, i) => {
            const meta = {
              facebook_ads: { label: 'FB Ads', cls: 'bg-[#1877F2]/15 hover:bg-[#1877F2]/25 border-[#1877F2]/40 text-[#60A5FA]' },
              google:       { label: 'Google', cls: 'bg-white/5 hover:bg-white/10 border-white/20 text-white/70' },
              youtube_ads:  { label: 'YT Ads', cls: 'bg-[#FF0000]/10 hover:bg-[#FF0000]/20 border-[#FF0000]/40 text-[#F87171]' },
              tiktok:       { label: 'TikTok', cls: 'bg-[#25F4EE]/10 hover:bg-[#25F4EE]/20 border-[#25F4EE]/30 text-[#5EEAD4]' },
              tiktok_ads:   { label: 'TikTok', cls: 'bg-[#25F4EE]/10 hover:bg-[#25F4EE]/20 border-[#25F4EE]/30 text-[#5EEAD4]' },
            }[q.platform] || { label: q.platform, cls: 'bg-white/5 hover:bg-white/10 border-white/20 text-white/70' };
            return (
              <a
                key={i}
                href={q.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`discovery-result-query-${q.platform}`}
                title={q.label}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 border rounded-lg text-[10px] uppercase tracking-wider font-semibold transition ${meta.cls}`}
              >
                {meta.label}
              </a>
            );
          })}
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discovery-result-open-link"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-[10px] uppercase tracking-wider transition"
              title={t('disc.open_domain')}
            >
              Site <ArrowUpRight size={10} weight="bold" />
            </a>
          )}
          <button
            onClick={() => window.location.assign(`/winner-score?url=${encodeURIComponent(item.url || '')}&q=${encodeURIComponent(item.title || '')}`)}
            data-testid="discovery-result-winner-btn"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:opacity-90 rounded-lg text-[10px] font-bold text-black uppercase tracking-wider transition"
            title={t('disc.calc_winner')}
          >
            <Trophy size={11} weight="fill" /> Score
          </button>
          {item.url && (
            <button
              onClick={() => onAnalyze(item.url)}
              data-testid="discovery-result-analyze-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-lg text-[10px] uppercase tracking-wider font-semibold transition"
            >
              <Sparkle size={11} weight="fill" /> {t('disc.ai_btn')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Discovery = ({ user }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [niche, setNiche] = useState('');
  const [language, setLanguage] = useState('pt');
  const [country, setCountry] = useState('PT');
  const [includeAds, setIncludeAds] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/discovery/history`, { withCredentials: true });
      setHistory(res.data.items || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const search = useCallback(async (e) => {
    e?.preventDefault();
    if (!niche.trim()) return;
    setLoading(true);
    setResults(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/discovery/search`,
        { niche: niche.trim(), language, country, include_ads: includeAds },
        { withCredentials: true }
      );
      setResults(res.data);
      toast.success(t('disc.toast_found', { n: res.data.results.length, niche }));
      loadHistory();
    } catch (err) {
      const msg = err?.response?.data?.detail || t('disc.toast_fail');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [niche, language, country, includeAds, loadHistory, t]);

  const loadHistoryItem = async (discoveryId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/discovery/${discoveryId}`, { withCredentials: true });
      setResults(res.data);
      setNiche(res.data.niche);
      setLanguage(res.data.language);
      setCountry(res.data.country);
    } catch (e) {
      toast.error(t('disc.toast_open_fail'));
    } finally {
      setLoading(false);
    }
  };

  const sendToFunnelSpy = (url) => {
    navigate(`/funnel-spy?url=${encodeURIComponent(url)}`);
  };

  // Compute Top Picks (top 3 by score) from both live ads + curated results, combined.
  // Curated items in topPicks are removed from the main grid below to avoid duplication.
  const { topPicks, curatedKeysInTop } = useMemo(() => {
    if (!results) return { topPicks: [], curatedKeysInTop: new Set() };
    const live = (results.live_ads || []).map((a) => ({
      kind: 'live',
      key: `live-${a.ad_id}`,
      title: a.page_name || t('disc.advertiser_page'),
      subtitle: a.ad_link_title || (a.ad_body ? a.ad_body.slice(0, 90) + '…' : t('disc.active_ad')),
      score: a.estimated_score || 0,
      url: a.snapshot_url,
      badge: 'LIVE',
    }));
    const curated = (results.results || []).map((r, i) => ({
      kind: 'curated',
      key: `curated-${i}`,
      index: i,
      title: r.title,
      subtitle: r.brand_or_creator || r.why_modelable?.slice(0, 90),
      score: r.estimated_score || 0,
      url: (r.enriched_queries && r.enriched_queries[0]?.url) || null,
      badge: (r.type || 'funil').toUpperCase().replace('_', ' '),
    }));
    const top = [...live, ...curated]
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
    const curIndices = new Set(top.filter((t) => t.kind === 'curated').map((t) => t.index));
    return { topPicks: top, curatedKeysInTop: curIndices };
  }, [results, t]);

  return (
    <DashboardLayout user={user} activePage="/discovery">
      <div className="p-6" data-testid="discovery-page">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#A855F7]/15 border border-[#A855F7]/30 rounded-full mb-3">
            <Robot size={14} weight="fill" className="text-[#A855F7]" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A855F7] font-semibold">IA-Curated · GPT-5.2</span>
          </div>
          <h1 className="text-4xl font-[Outfit] font-bold tracking-tight mb-2">{t('disc.title')}</h1>
          <p className="text-white/60 max-w-2xl">
            {t('disc.subtitle')}
          </p>
        </div>

        {/* Search form */}
        <form onSubmit={search} className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl mb-6" data-testid="discovery-search-form">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('disc.niche_label')}</label>
              <div className="relative">
                <Target size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  placeholder={t('disc.niche_ph')}
                  data-testid="discovery-niche-input"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]/50 transition"
                  required
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('disc.language')}</label>
              <div className="relative">
                <Translate size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="discovery-language-select"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]/50 transition appearance-none cursor-pointer"
                >
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code} className="bg-[#0A0A0A]">{l.label}</option>)}
                </select>
              </div>
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('disc.country')}</label>
              <div className="relative">
                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="discovery-country-select"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]/50 transition appearance-none cursor-pointer"
                >
                  {COUNTRY_CODES.map((c) => <option key={c} value={c} className="bg-[#0A0A0A]">{t(`disc.country_${c.toLowerCase()}`)}</option>)}
                </select>
              </div>
            </div>
            <div className="lg:col-span-2 flex items-end">
              <button
                type="submit"
                disabled={loading || !niche.trim()}
                data-testid="discovery-search-btn"
                className="w-full px-5 py-3 bg-gradient-to-r from-[#A855F7] to-[#7C3AED] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
              >
                {loading ? 'A descobrir...' : (
                  <>
                    <MagnifyingGlass size={16} weight="bold" />
                    Descobrir
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Quick suggestions */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wider text-white/30">{t('disc.suggestions')}</span>
            {NICHE_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setNiche(t(`disc.sug_${s}`))}
                data-testid={`discovery-suggestion-${s.replace(/_/g, '-')}`}
                className="px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-[11px] text-white/60 transition"
              >
                {t(`disc.sug_${s}`)}
              </button>
            ))}
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-white/60 cursor-pointer w-fit" data-testid="discovery-include-ads-label">
            <input
              type="checkbox"
              checked={includeAds}
              onChange={(e) => setIncludeAds(e.target.checked)}
              className="accent-[#A855F7]"
            />
            {t('disc.include_ads')}
          </label>
        </form>

        {/* Loading state */}
        {loading && !results && (
          <div className="p-10 bg-[#0A0A0A] border border-white/10 rounded-2xl text-center" data-testid="discovery-loading">
            <Lightning size={32} weight="fill" className="mx-auto mb-4 text-[#A855F7] animate-pulse" />
            <p className="text-white/70">{t('disc.loading_pre')} <strong className="text-white">{niche}</strong>...</p>
            <p className="text-white/40 text-sm mt-2">{t('disc.loading_time')}</p>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Star size={18} weight="fill" className="text-[#F59E0B]" />
                  <span className="text-sm text-white/60">{t('disc.results_for')}</span>
                </div>
                <h2 className="text-2xl font-[Outfit] font-bold">
                  &ldquo;{results.niche}&rdquo; · {results.country ? t(`disc.country_${results.country.toLowerCase()}`, { defaultValue: results.country }) : ''}
                </h2>
              </div>
              <div className="text-sm text-white/40">
                {t('disc.funnels_sources', { n: results.results?.length || 0, m: results.ad_library_links?.length || 0 })}
              </div>
            </div>

            {/* TOP PICKS · highest-potential funnels (live + AI-curated combined, no duplicates in grid below) */}
            {topPicks.length > 0 && (
              <div className="p-5 bg-gradient-to-br from-[#F59E0B]/15 via-[#F59E0B]/5 to-transparent border border-[#F59E0B]/40 rounded-2xl" data-testid="discovery-top-picks">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy size={20} weight="fill" className="text-[#F59E0B]" />
                  <h3 className="font-[Outfit] font-bold text-lg">{t('disc.top_picks')}</h3>
                  <span className="px-2 py-0.5 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded text-[10px] font-semibold text-[#F59E0B] uppercase tracking-wider">
                    {topPicks.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {topPicks.map((p, idx) => {
                    const color = scoreColor(p.score);
                    return (
                      <div key={p.key} className="relative p-4 bg-[#0A0A0A] border border-white/10 rounded-xl hover:border-[#F59E0B]/40 transition" data-testid={`top-pick-${idx}`}>
                        <div className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#FBBF24] flex items-center justify-center text-black font-bold text-xs shadow-lg shadow-amber-500/30">
                          {idx + 1}
                        </div>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold"
                            style={{
                              background: p.kind === 'live' ? '#10B98122' : '#A855F722',
                              color: p.kind === 'live' ? '#10B981' : '#A855F7',
                            }}>
                            {p.badge}
                          </span>
                          <div className="flex flex-col items-end leading-none">
                            <span className="text-lg font-[Outfit] font-bold" style={{ color }}>{p.score}</span>
                            <span className="text-[8px] uppercase tracking-wider text-white/40">score</span>
                          </div>
                        </div>
                        <h4 className="font-[Outfit] font-bold text-sm text-white line-clamp-2 mb-1">{p.title}</h4>
                        {p.subtitle && (
                          <p className="text-[11px] text-white/50 line-clamp-2 leading-snug mb-3">{p.subtitle}</p>
                        )}
                        {p.url && (
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`top-pick-open-${idx}`}
                            title={p.kind === 'live' ? t('disc.creative_tip_live') : t('disc.creative_tip_curated')}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-[10px] uppercase tracking-wider transition"
                          >
                            {p.kind === 'live' ? t('disc.see_creative') : t('disc.search_fb')} <ArrowUpRight size={10} weight="bold" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
                  {t('disc.top_note')}
                </p>
              </div>
            )}

            {/* Live ads from Meta Ad Library (real, active right now) */}
            {Array.isArray(results.live_ads) && results.live_ads.length > 0 && (
              <div className="p-5 bg-gradient-to-br from-[#10B981]/10 to-transparent border border-[#10B981]/30 rounded-2xl" data-testid="discovery-live-ads">
                <div className="flex items-center gap-2 mb-3">
                  <Megaphone size={18} weight="bold" className="text-[#10B981]" />
                  <h3 className="font-[Outfit] font-bold">{t('disc.live_ads_title')}</h3>
                  <span className="px-2 py-0.5 bg-[#10B981]/15 border border-[#10B981]/30 rounded text-[10px] font-semibold text-[#10B981] uppercase tracking-wider">
                    {t('disc.active_count', { n: results.live_ads.length })}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {results.live_ads.map((ad) => {
                    const lScore = ad.estimated_score || 0;
                    const lColor = scoreColor(lScore);
                    return (
                    <div key={ad.ad_id} className="p-4 bg-[#0A0A0A] border border-white/10 rounded-xl" data-testid={`live-ad-${ad.ad_id}`}>
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-[#1877F2]/20 flex items-center justify-center flex-shrink-0">
                            <Megaphone size={12} weight="bold" className="text-[#60A5FA]" />
                          </div>
                          <span className="text-sm font-[Outfit] font-semibold truncate">{ad.page_name || t('disc.page_fallback')}</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                            data-testid={`live-ad-source-${ad.ad_id}`}
                            style={ad.source === 'youtube'
                              ? { color: '#F87171', background: '#FF000018', borderColor: '#FF000055' }
                              : ad.source === 'tiktok'
                                ? { color: '#5EEAD4', background: '#25F4EE15', borderColor: '#25F4EE44' }
                                : { color: '#60A5FA', background: '#1877F218', borderColor: '#1877F255' }}
                          >
                            {ad.source === 'youtube' ? 'YouTube' : ad.source === 'tiktok' ? 'TikTok' : 'Meta'}
                          </span>
                          <span className="text-[9px] uppercase tracking-wider text-[#10B981] px-1.5 py-0.5 bg-[#10B981]/10 border border-[#10B981]/30 rounded">LIVE</span>
                          <div
                            className="flex flex-col items-center justify-center px-1.5 py-0.5 rounded border leading-none"
                            style={{ background: `${lColor}11`, borderColor: `${lColor}44` }}
                            data-testid={`live-ad-score-${ad.ad_id}`}
                            title={t('disc.score_tip')}
                          >
                            <span className="text-sm font-[Outfit] font-bold" style={{ color: lColor }}>{lScore}</span>
                            <span className="text-[7px] uppercase tracking-wider text-white/40">score</span>
                          </div>
                        </div>
                      </div>
                      {ad.ad_body && (
                        <p className="text-xs text-white/70 line-clamp-3 leading-relaxed mb-2">{ad.ad_body}</p>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <span className="text-[10px] text-white/40">
                          {t('disc.active_since')} {ad.started_at ? new Date(ad.started_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-US') : '—'}
                        </span>
                        {ad.snapshot_url && (
                          <a
                            href={ad.snapshot_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`live-ad-open-${ad.ad_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-white/5 hover:bg-white/10 rounded text-[10px] uppercase tracking-wider transition"
                          >
                            {t('disc.view')} <ArrowUpRight size={10} weight="bold" />
                          </a>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty state: live ads requested but none passed niche relevance filter */}
            {Array.isArray(results.live_ads) && results.live_ads.length === 0 && (
              <div className="p-5 bg-gradient-to-br from-[#A855F7]/10 to-transparent border border-[#A855F7]/25 rounded-2xl" data-testid="discovery-live-ads-empty">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#A855F7]/15 flex items-center justify-center flex-shrink-0">
                    <Sparkle size={16} weight="fill" className="text-[#A855F7]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-[Outfit] font-bold text-sm mb-1">{t('disc.live_empty_title')}</h3>
                    <p className="text-xs text-white/60 leading-relaxed">
                      {t('disc.live_empty_p1', { niche: results.niche })}
                      <br /><br />
                      <strong className="text-[#A855F7]">{t('disc.live_empty_p2')}</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Manual ad-library shortcuts: safety net when AI-curated results aren't enough.
                Re-labeled to clearly distinguish from the actual results. */}
            {results.ad_library_links && results.ad_library_links.length > 0 && (
              <div className="p-5 bg-gradient-to-br from-[#1877F2]/8 to-transparent border border-[#1877F2]/20 rounded-2xl" data-testid="discovery-ad-libraries">
                <div className="flex items-start gap-2 mb-1">
                  <Megaphone size={16} weight="bold" className="text-[#1877F2] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-[Outfit] font-semibold text-sm text-white/80">{t('disc.shortcuts_title')}</h3>
                    <p className="text-[11px] text-white/40 leading-snug">{t('disc.shortcuts_sub')}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {results.ad_library_links.map((ad, i) => (
                    <a
                      key={i}
                      href={ad.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`discovery-ad-link-${ad.platform}`}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border border-[#1877F2]/30 rounded-lg text-xs transition"
                    >
                      {ad.label}
                      <ArrowUpRight size={12} weight="bold" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Grid of funnel cards (curated by AI). Excludes those already in Top Picks. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {(results.results || []).map((item, idx) => (
                curatedKeysInTop.has(idx) ? null : (
                  <ResultCard key={idx} item={item} onAnalyze={sendToFunnelSpy} />
                )
              ))}
            </div>

            {(!results.results || results.results.length === 0) && (
              <div className="p-8 bg-[#121212] border border-white/10 rounded-2xl text-center" data-testid="discovery-empty">
                <Flask size={32} weight="bold" className="mx-auto mb-3 text-white/30" />
                <p className="text-white/60">{t('disc.empty_result')}</p>
              </div>
            )}
          </div>
        )}

        {/* History */}
        {!results && history.length > 0 && (
          <div className="mt-8" data-testid="discovery-history">
            <h3 className="text-sm uppercase tracking-[0.2em] text-white/40 mb-3">{t('disc.history_title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.map((h) => (
                <button
                  key={h.discovery_id}
                  onClick={() => loadHistoryItem(h.discovery_id)}
                  data-testid={`discovery-history-${h.discovery_id}`}
                  className="text-left p-4 bg-[#121212] border border-white/10 rounded-xl hover:border-white/20 transition"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Tag size={14} weight="bold" className="text-[#A855F7]" />
                    <span className="text-xs text-white/40 uppercase tracking-wider">{h.country} · {h.language.toUpperCase()}</span>
                  </div>
                  <div className="font-[Outfit] font-bold text-white truncate">{h.niche}</div>
                  <div className="text-xs text-white/50 mt-1">{t('disc.funnels_count', { n: h.results_count })} · {new Date(h.created_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Discovery;

